/* eslint-disable no-console */
import httpStatus from "http-status";

import {
	authInterceptorFactory,
	axios,
	interceptAndNormalizeResponse,
	piggyBack,
	requestHeaderNormalizerInterceptor,
	RetryRequest
} from "@teaming/io-helpers";

const IO_NAMESPACE = "stream";

// eslint-disable-next-line no-unused-vars
export default function ioStreamFactory( { config: { baseUri, showTeamlessTestCheckbox, }, authService, telemetry, i18n, } ) {
	axios.defaults.baseURL = baseUri;
	axios.interceptors.request.use( requestHeaderNormalizerInterceptor );
	axios.interceptors.request.use( authInterceptorFactory( { authService, } ) );
	axios.interceptors.response.use( ...interceptAndNormalizeResponse() );

	const sendStreamNoteSyncNotice = piggyBack( async ( { documentName, subDocId, } ) => {
		const url = `/stream/${ documentName }/subdoc/${ subDocId }/sync`;
		return axios.post( url, {} );
	}, { timeout: 500, } );

	const startTranscription = async ( { documentName, subDocId, subDocTitle, subDocLink, formFile, } ) => {
		const uploadName = formFile.name;
		const extension = ( uploadName.split( "." ) ).pop();
		const url = [
			`/stream/${ documentName }/subdoc/${ subDocId }/upload-url?`,
			`subDocTitle=${ encodeURIComponent( subDocTitle ) }&`,
			`subDocLink=${ encodeURIComponent( subDocLink ) }&`,
			`extension=${ extension }`,
		].join( "" ).trim();
		const response = await axios.get( url );
		const signedUrl = response.data;
		return fetch( signedUrl, {
			method: "PUT",
			body: formFile,
		} );
	};

	const deployTranscriptionBot = async ( { documentName, subDocId, subDocTitle, subDocLink, meetingURL, attempt = 1, managedBy } ) => {
		const url = `/stream/${ documentName }/subdoc/${ subDocId }/bot?attempt=${ attempt }`;
		const body = {
			documentName,
			subDocId,
			subDocTitle,
			subDocLink,
			meetingURL,
			managedBy
		};
		return axios.post( url, body );
	};

	const tryDeployTranscriptionBot = async ( { documentName, subDocId, subDocTitle, subDocLink, meetingURL, managedBy } ) => {
		return new RetryRequest( {
			ioMethod: deployTranscriptionBot,
			params: {
				documentName,
				subDocId,
				subDocTitle,
				subDocLink,
				meetingURL,
				attempt: 1,
				managedBy
			},
			successStatuses: [ httpStatus.NO_CONTENT, httpStatus.CREATED, ],
			retryStatuses: [ httpStatus.SERVICE_UNAVAILABLE, ],
			maxRetryAttempts: 5,
			waitPeriodSeconds: 30,
		} );
	};

	const fetchSubDocSupplement = async ( { documentName, subDocId, }, { withSummary = false, withTranscription = false, } = {} ) => {
		let url = `/stream/${ documentName }/subdoc/${ subDocId }/supplement`;
		const options = [
			withSummary && ".summary",
			withTranscription && ".transcription",
		].filter( o => !!o );
		if ( options.length ) {
			url = `${ url }?${ options.join( "&" ) }`;
		}
		return axios.get( url );
	};

	const fetchDocumentSupplements = async ( { documentName, }, { withSummaries = false, withTranscriptions = false, } = {} ) => {
		let url = `/stream/${ documentName }/supplement`;
		const options = [
			withSummaries && ".summary",
			withTranscriptions && ".transcription",
		].filter( o => !!o );
		if ( options.length ) {
			url = `${ url }?${ options.join( "&" ) }`;
		}
		return axios.get( url );
	};

	const fetchTranscription = piggyBack( async ( { documentName, subDocId, } ) => {
		const response = await fetchSubDocSupplement( { documentName, subDocId, }, { withTranscription: true, } );
		// backwards compatibility for now
		return {
			...response,
			data: {
				subDocId,
				speechBlocks: response.data.transcription,
			},
		};
	} );

	const rateTranscription = piggyBack( async ( { documentName, subDocId, transcriptionJobId, rating, } ) => {
		const url = `/stream/${ documentName }/subdoc/${ subDocId }/transcription/${ transcriptionJobId }/rating`;
		const body = { rating, };
		return axios.put( url, body );
	} );

	const abortTranscription = piggyBack( async ( { documentName, subDocId, transcriptionJobId, } ) => {
		const url = `/stream/${ documentName }/subdoc/${ subDocId }/transcription/${ transcriptionJobId }`;
		return axios.delete( url );
	} );

	const updateTranscription = async ( { documentName, subDocId, transcriptionJobId, joinAt, } ) => {
		const url = `/stream/${ documentName }/subdoc/${ subDocId }/transcription/${ transcriptionJobId }`;
		// currently only supporting a change in the "join at" time; this will only take
		// effect if the job is queued
		const body = { joinAt, };
		return axios.put( url, body );
	};

	const supplementSubDoc = async ( { documentName, subDocId, date, enableAssistant, } ) => {
		const url = `/stream/${ documentName }/subdoc/${ subDocId }/supplement`;
		return axios.put( url, {
			date,
			enableAssistant,
		} );
	};

	const fetchRecurrences = async ( { documentName, recurrenceId = "", } ) => {
		let url = `/stream/${ documentName }/recurrence`;
		if ( recurrenceId ) {
			url = `${ url }/${ recurrenceId }`;
		}
		return axios.get( url );
	};

	const saveRecurrence = async ( { recurrence, } ) => {
		const { documentName, teamlessField, } = recurrence;
		const url = `/stream/${ documentName }/recurrence`;
		if ( !recurrence.id ) {
			if ( teamlessField && showTeamlessTestCheckbox ) {
				return axios.post( '/teamless-meeting', recurrence );
			}
			return axios.post( url, recurrence );
		}
		return axios.put( `${ url }/${ recurrence.id }`, recurrence );
	};

	const deleteRecurrence = async ( { documentName, recurrenceId, } ) => {
		const url = `/stream/${ documentName }/recurrence/${ recurrenceId }`;
		return axios.delete( url );
	};

	const deleteRecurrenceEntry = async ( { documentName, entryId, recurrenceId, } ) => {
		const url = `/stream/${ documentName }/recurrence/${ recurrenceId }/entry/${ entryId }`;
		return axios.delete( url );
	};

	return {
		namespace: IO_NAMESPACE,
		abortTranscription,
		deleteRecurrence,
		deleteRecurrenceEntry,
		deployTranscriptionBot,
		fetchDocumentSupplements,
		fetchRecurrences,
		fetchSubDocSupplement,
		fetchTranscription,
		rateTranscription,
		saveRecurrence,
		sendStreamNoteSyncNotice,
		startTranscription,
		supplementSubDoc,
		tryDeployTranscriptionBot,
		updateTranscription,
	};
}
